import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { ChatTemperature, formatPrice, formatWhen, leadTemperature, roleLabel, formatAgo } from '../../chat.format';
import { CHAT_STRINGS_PT, ChatStrings } from '../../chat.strings';
import { ChatConversation, ChatDealStage, ChatDealStages, ChatMessageProperty, ChatNextAction, ChatPresenceStatus, ChatPendingInvite } from '../../chat.model';

/** One colleague of the "Colegas na conversa" section. */
export interface ChatDetailsColleague {
  id: string;
  name: string;
  photo?: string;
  role: string;
  responsible: boolean;
  me: boolean;
}

/** One "label · value" line of the info section. */
export interface ChatDetailsRow {
  label: string;
  value: string;
  tone?: 'online' | 'away';
  /** mailto: / tel: quando a linha é um contacto. */
  href?: string;
}

/**
 * Right-hand column of the conversation (reference layout): big avatar,
 * name, role · presence, quick actions (open the property in the CRM, on the
 * agency site, invite), "Informações" (only the lines that have data: role,
 * state, responsible, property), "Colegas na conversa" (participants with
 * Responsável / Convidado and a dashed "Convidar colega"), "Imóvel da
 * conversa" and the last property cards sent in the thread. "Cliente" (email,
 * phone, origin, budget, lead temperature), "Negócio" (stage, advance) and
 * "Próxima ação" (reminder / visit) come from the lead linked to the chat
 * (v3) and only exist in a client conversation (`canRemind`).
 *
 * `profileBase` turns a participant's `profile` (a file name) into a URL,
 * like the container does for the list rows.
 */
@Component({
  selector: 'chat-details-panel',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './chat-details-panel.component.html',
  styleUrls: ['./chat-details-panel.component.css']
})
export class ChatDetailsPanelComponent implements OnChanges {
  @Input() conversation: ChatConversation | null = null;
  @Input() meId?: string;
  @Input() presence: ChatPresenceStatus | null = null;
  @Input() canInvite = false;
  @Input() propertyRoute: any[] | null = null;
  @Input() propertyQueryParams: { [key: string]: any } | null = null;
  @Input() profileBase = '';
  @Input() strings: ChatStrings = CHAT_STRINGS_PT;

  @Output() invite = new EventEmitter<void>();
  @Output() close = new EventEmitter<void>();
  /* 7.3: negócio, próxima ação e transferência */
  @Input() dealStages: ChatDealStages | null = null;
  @Input() nextAction: ChatNextAction | null = null;
  @Input() canTransfer = false;
  @Input() stageBusy = false;
  @Output() advanceStage = new EventEmitter<ChatDealStage>();
  /** 7.1: retirar um colega (responsável / quem transfere) e sair eu (convidado). */
  @Input() canRemoveParticipants = false;
  @Input() canLeave = false;
  @Output() removeParticipant = new EventEmitter<string>();
  @Output() leave = new EventEmitter<void>();
  /** Conversa de cliente (não supervisionada): a secção "Próxima ação" e o lembrete existem. */
  @Input() canRemind = false;
  /** Só numa conversa de cliente: entre colegas não há "colegas na conversa" (a conversa é a dois). */
  @Input() showColleagues = true;
  /** Contrato 6.3, v8: convidados que ainda nao responderam (com "Cancelar"). */
  @Input() pendingInvites: ChatPendingInvite[] = [];
  @Output() cancelInvite = new EventEmitter<string>();
  @Output() addReminder = new EventEmitter<void>();
  @Output() transfer = new EventEmitter<void>();

  roleText = '';
  presenceText = '';
  rows: ChatDetailsRow[] = [];
  /** v3: linhas do cliente (email, telefone, origem, orçamento) e temperatura da lead. */
  clientRows: ChatDetailsRow[] = [];
  temperature: ChatTemperature | null = null;
  temperatureText = '';
  dealStageText = '';
  colleagues: ChatDetailsColleague[] = [];
  sentProperties: ChatMessageProperty[] = [];

  ngOnChanges(): void {
    const conv = this.conversation;
    if (!conv) {
      this.rows = [];
      this.clientRows = [];
      this.temperature = null;
      this.temperatureText = '';
      this.dealStageText = '';
      this.colleagues = [];
      this.sentProperties = [];
      this.roleText = '';
      this.presenceText = '';
      return;
    }
    this.buildClientRows(conv);
    this.roleText = roleLabel(conv.role, this.strings);
    this.presenceText = this.presence === 'online'
      ? this.strings.onlineNow
      : this.presence === 'away' ? this.strings.presenceAwayShort : '';

    const rows: ChatDetailsRow[] = [];
    if (this.roleText) {
      rows.push({ label: this.strings.roleLabel, value: this.roleText });
    }
    if (this.presenceText) {
      rows.push({ label: this.strings.stateLabel, value: this.presenceText, tone: this.presence === 'online' ? 'online' : 'away' });
    }
    const responsible = this.responsibleName(conv);
    if (responsible) {
      rows.push({ label: this.strings.responsibleLabel, value: responsible });
    }
    if (conv.propertyReference) {
      rows.push({ label: this.strings.propertyLabel, value: conv.propertyReference });
    }
    this.rows = rows;

    this.colleagues = (conv.participants || [])
      .filter(p => String(p.id) !== String(conv.userId) && (!!p.name || String(p.id) === this.meId))
      .map(p => {
        const id = String(p.id);
        const responsible = !!conv.assignedUserAppId && String(conv.assignedUserAppId) === id;
        const me = id === this.meId;
        return {
          id,
          name: me ? this.strings.participantYou : String(p.name || ''),
          photo: this.photoOf(p.profile),
          role: responsible ? this.strings.participantResponsible : this.strings.participantInvited,
          responsible,
          me
        };
      })
      .sort((a, b) => (a.responsible === b.responsible ? 0 : a.responsible ? -1 : 1));

    const seen: { [id: string]: boolean } = {};
    const out: ChatMessageProperty[] = [];
    for (let i = conv.messages.length - 1; i >= 0 && out.length < 3; i--) {
      const m = conv.messages[i];
      if (m.kind === 'property' && m.property && !seen[String(m.property.id)]) {
        seen[String(m.property.id)] = true;
        out.push(m.property);
      }
    }
    this.sentProperties = out;
  }

  /** Cliente, lead e negócio (v3). Só o que existe; o orçamento em "min – max €". */
  private buildClientRows(conv: ChatConversation): void {
    const rows: ChatDetailsRow[] = [];
    const client = conv.client;
    if (client) {
      if (client.email) {
        rows.push({ label: this.strings.emailLabel, value: client.email, href: 'mailto:' + client.email });
      }
      if (client.phone) {
        rows.push({ label: this.strings.phoneLabel, value: client.phone, href: 'tel:' + String(client.phone).replace(/\s+/g, '') });
      }
      if (client.source) {
        rows.push({ label: this.strings.sourceLabel, value: this.sourceText(client.source) });
      }
      const budget = [client.budgetMin, client.budgetMax].filter(v => v !== undefined && v !== null && v > 0).map(v => formatPrice(v));
      if (budget.length > 0) {
        rows.push({ label: this.strings.budgetLabel, value: budget.join(' – ') });
      }
    }
    if (conv.lead && conv.lead.score !== undefined && conv.lead.score !== null) {
      rows.push({ label: this.strings.scoreLabel, value: String(Math.round(conv.lead.score)) + ' / 100' });
    }
    this.clientRows = rows;
    this.temperature = conv.lead ? leadTemperature(conv.lead.category) : null;
    this.temperatureText = this.temperature === 'hot' ? this.strings.temperatureHot
      : this.temperature === 'warm' ? this.strings.temperatureWarm
      : this.temperature === 'cold' ? this.strings.temperatureCold : '';
    const deal = conv.deal;
    this.dealStageText = !deal ? ''
      : deal.isWon ? this.strings.dealWon
      : deal.isLost ? this.strings.dealLost
      : (deal.statusName || '');
  }

  /** `t-sources.t-website` → "Website"; um nome simples passa tal como vem. */
  private sourceText(source: string): string {
    const last = String(source).split('.').pop() || source;
    const clean = last.replace(/^t-/, '').replace(/-/g, ' ');
    return clean.charAt(0).toUpperCase() + clean.slice(1);
  }

  get nextActionText(): string {
    const n = this.nextAction;
    if (!n) {
      return '';
    }
    const when = formatWhen(n.at);
    return (n.type === 'visit' ? this.strings.nextActionVisit : (n.title || this.strings.addReminder)) + ' · ' + when;
  }

  get advanceLabel(): string {
    const next = this.dealStages && this.dealStages.next;
    return next ? this.strings.advanceStageTo.replace('{name}', next.name) : '';
  }

  /** "a espera de resposta · ha 5 min" */
  waitingText(p: ChatPendingInvite): string {
    return this.strings.inviteWaiting.replace('{time}', formatAgo(p.invitedAt, this.strings));
  }

  invitePhoto(profile?: string): string | undefined {
    if (!profile) {
      return undefined;
    }
    return /^(https?:)?\//.test(profile) || profile.indexOf('data:') === 0 ? profile : this.profileBase + profile;
  }

  trackByInvite(_: number, p: ChatPendingInvite): string {
    return p.userAppId;
  }

  trackByColleague = (_i: number, c: ChatDetailsColleague) => c.id;
  trackByProperty = (_i: number, p: ChatMessageProperty) => p.id;

  priceOf(p: ChatMessageProperty): string {
    const v = p.price;
    if (v === undefined || v === null || v === '') {
      return '';
    }
    if (typeof v === 'number') {
      return String(Math.round(v)).replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + ' €';
    }
    return String(v);
  }

  private responsibleName(conv: ChatConversation): string {
    if (conv.supervised && conv.ownerName) {
      return conv.ownerName;
    }
    if (!conv.assignedUserAppId) {
      return '';
    }
    if (String(conv.assignedUserAppId) === this.meId) {
      return this.strings.participantYou;
    }
    return conv.assignedName || '';
  }

  private photoOf(profile?: string): string | undefined {
    if (!profile) {
      return undefined;
    }
    if (/^(https?:)?\//.test(profile) || profile.indexOf('data:') === 0) {
      return profile;
    }
    return this.profileBase ? this.profileBase + profile : undefined;
  }
}
